import { createEffect } from "solid-js"
import { ConeGeometry, FrontSide, Group, LOD, Mesh, MeshLambertMaterial, PlaneGeometry, type BufferGeometry, type Scene } from "three"
import { onCleanupRemoveAndDispose } from "../../../../rx/cleanup/onCleanupRemoveAndDispose"
import { createSharedTileGraphNodeCollectionMemo } from "../../../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import { createTriggerSignal } from "../../../../rx/signal/create_trigger_signal"
import type { Engine } from "../../../core/engine/Engine.type"
import type { TileGraphNode } from "../../../core/engine/TileGraphNode/TileGraphNode.type"
import type { QuadrantBound } from "../../../Quadtree/createEntityQuadtreeEffect"
import createTileGraphNodeQuadtree from "../../../Quadtree/createTileGraphNodeQuadtree"
import { VERTICES_PER_TILE } from "../../consts"
import rotatePlaneToLayFlat from "../../fn/rotatePlaneToLayFlat"
import { useRootQuadrantBound } from "../../sky/useRootQuadrantBound"
import createTerrainMesh1V3TileFloorGroup from "./TileFloor/createTerrainMesh1V3TileFloorGroup"
import createTerrainMesh1V3TileFloorLowMesh from "./TileFloor/createTerrainMesh1V3TileFloorLowMesh"

const USE_LOD_DEBUG_CONE = false
const USE_LOD_DEBUG_PLANE = false

export default function createTerrainMesh1V3QuadTreeEffect(
  engine: Engine,
  scene: Scene,
) {
  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)
  // const getGridSize = useGridWidthHeight(engine)
  const getRootQuadrantBound = useRootQuadrantBound(engine)
  const maxChunkSize = 4
  createEffect(() => {
    const tpNodeList = getTileGraphNodesList()
    const rootQuadrant = getRootQuadrantBound()
    const outerGroup = new Group()
    createTileGraphNodeQuadtree(tpNodeList, rootQuadrant, maxChunkSize, (
      subNodeList: Array<TileGraphNode>,
      quadrant: QuadrantBound,
    ) => {
      // console.log('createTerrainMesh1V3QuadTreeEffect', quadrant, subNodeList.length)

      const lod = new LOD()
      onCleanupRemoveAndDispose(lod)
      lod.position.set((quadrant.x + 1.5) * VERTICES_PER_TILE, 0, (quadrant.y + 1.5) * VERTICES_PER_TILE)
      outerGroup.add(lod)

      const geometryLowList: Array<BufferGeometry> = []
      const [trackGeometryLowList, dirtyGeometryLowList] = createTriggerSignal()
      const materialList: Array<MeshLambertMaterial> = []
      const [trackMaterialList, dirtyMaterialList] = createTriggerSignal()
      // closest LOD
      {
        const lodDistance = 0
        const quadGroup = createTerrainMesh1V3TileFloorGroup(engine, subNodeList, quadrant, geometryLowList, dirtyGeometryLowList, materialList, dirtyMaterialList)
        rotatePlaneToLayFlat(quadGroup)
        quadGroup.position.x -= (1.5 * VERTICES_PER_TILE)
        quadGroup.position.z -= (1.5 * VERTICES_PER_TILE)
        lod.addLevel(quadGroup, lodDistance)
      }

      // middle LOD
      {
        const lodDistance = 200
        // const lodDistance = 100
        // const lodDistance = 40
        if (USE_LOD_DEBUG_PLANE) {
          const geometry = new PlaneGeometry(
            VERTICES_PER_TILE * quadrant.width, VERTICES_PER_TILE * quadrant.height,
            quadrant.width, quadrant.height)
          rotatePlaneToLayFlat(geometry)

          const material = new MeshLambertMaterial({
            color: 0x774444,
            side: FrontSide,
            // transparent: true,
            // opacity: 0.75,
          })
          const mesh = new Mesh(geometry, material)
          // mesh.position.x -= (1.5 * VERTICES_PER_TILE)
          mesh.position.y += 1
          // mesh.position.z -= (1.5 * VERTICES_PER_TILE)
          lod.addLevel(mesh, lodDistance)
        } else {
          const quadMesh = createTerrainMesh1V3TileFloorLowMesh(
            engine,
            quadrant,
            geometryLowList,
            trackGeometryLowList,
            materialList,
            trackMaterialList,
          )
          rotatePlaneToLayFlat(quadMesh)
          quadMesh.position.x -= (1.5 * VERTICES_PER_TILE)
          quadMesh.position.z -= (1.5 * VERTICES_PER_TILE)
          lod.addLevel(quadMesh, lodDistance)
          onCleanupRemoveAndDispose(quadMesh)
          // createMountedInterval(() => {
          //   // console.log('lod.levels', lod.levels)
          //   if (lod.levels[1].distance === 1) {
          //     lod.levels[1].distance = 100
          //   } else {
          //     lod.levels[1].distance = 1
          //   }
          // }, 2000)
        }
      }

      // furthest away LOD
      // {
      //   // lod.addLevel(quadGroup, 250)
      // }

      if (USE_LOD_DEBUG_CONE) {
        const mesh = new Mesh(new ConeGeometry(), new MeshLambertMaterial({ color: 0xffffff }))
        outerGroup.add(mesh)
        mesh.position.copy(lod.position)
        mesh.position.y += 2
        mesh.scale.setScalar(5)
      }
    })
    scene.add(outerGroup)
    // group.position.x += VERTICES_PER_TILE * 1.5
    // group.position.z += VERTICES_PER_TILE * 1.5
    // outerGroup.position.y += VERTICES_PER_TILE * 0.1
    onCleanupRemoveAndDispose(outerGroup)
  })
}